import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  format,
  isLastDayOfMonth,
  isSameMonth,
  isSameWeek,
  isSameYear,
  lastDayOfWeek,
  lastDayOfYear,
} from 'date-fns'
import { EmployeeTimePeriodDetailsFragment } from 'generated/resources/graphql'

import UserAvatar from 'modules/core/components/UserAvatar'
import { normalizeText } from 'modules/core/lib/stringUtils'

export const MAX_TO_SHOW = 8
export const ZERO_HOURS = '0.0'

/**
 * Round a numeric hours value to 2 decimal places.
 * Prevents IEEE 754 floating-point artifacts (e.g. 154.29000000000002)
 * when doing arithmetic on hours values received as strings from GraphQL.
 */
export const roundHours = (value: number): number => Math.round(value * 100) / 100

export enum RangeOptionsEnum {
  week = 'week',
  thisWeek = 'this week',
  month = 'month',
  thisMonth = 'this month',
  year = 'year',
  thisYear = 'this year',
  yearToDate = 'year to date',
}

export const getUserInfo = (employeeData: EmployeeTimePeriodDetailsFragment) => {
  const userData = {
    avatar: employeeData.employee.avatar?.url || '',
    fullName: employeeData.employeeFullName,
  }
  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Box>
        <UserAvatar size="s24" user={userData} />
      </Box>
      <Text.Normal size="200" weight="medium" ellipsis>
        {employeeData.employeeFullName}
      </Text.Normal>
    </Box>
  )
}

// For fragment ordering to work correctly with accented names.
export const sortByName = (item: EmployeeTimePeriodDetailsFragment) => {
  return normalizeText(item.employeeFullName)
}

export const findRangeType = (startDate: Date, endDate: Date): string => {
  // These dates include hours so we need to ignore them
  // to be able to compare them since they are different:
  // endDate returns Fri May 31 2024 23:59:59
  // lastDay returns Fri May 31 2024 00:00:00
  // the exception being the last day of the month since the date-fns library has a function for it

  const formattedEndDate = format(endDate, 'yyyy-MM-dd')
  const formattedLastDayOfTheWeek = format(lastDayOfWeek(endDate), 'yyyy-MM-dd')
  const formattedLastDayOfTheYear = format(lastDayOfYear(endDate), 'yyyy-MM-dd')

  if (
    isSameWeek(startDate, endDate) &&
    isSameWeek(startDate, Date.now()) &&
    formattedEndDate === formattedLastDayOfTheWeek
  ) {
    return RangeOptionsEnum.thisWeek
  }

  if (isSameWeek(startDate, endDate) && formattedEndDate === formattedLastDayOfTheWeek) {
    return RangeOptionsEnum.week
  }

  if (
    isSameMonth(startDate, endDate) &&
    isSameMonth(startDate, Date.now()) &&
    isLastDayOfMonth(endDate)
  ) {
    return RangeOptionsEnum.thisMonth
  }

  if (isSameMonth(startDate, endDate) && isLastDayOfMonth(endDate)) {
    return RangeOptionsEnum.month
  }

  if (
    isSameYear(startDate, endDate) &&
    formattedEndDate === formattedLastDayOfTheYear &&
    isSameYear(startDate, Date.now())
  ) {
    return RangeOptionsEnum.thisYear
  }

  if (
    isSameYear(startDate, endDate) &&
    formattedEndDate === formattedLastDayOfTheYear &&
    !isSameYear(startDate, Date.now())
  ) {
    return RangeOptionsEnum.year
  }

  return RangeOptionsEnum.yearToDate
}
